import { snakeCase } from 'lodash';

import {
  IUomConversionAuditBulkConversionResult,
  IUomConversionAuditBulkSetUpPayload,
  IUomConversionAuditBulkSetUpResult,
  IUomConversionAuditBulkUpdatePayload,
  IUomConversionAuditBulkUpdateResult,
  IUomConversionAuditConversionPayload,
  IUomConversionAuditReport,
  IUomConversionAuditReportRequestPayload,
} from 'companySettings/types';
import { mapRecordFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetOneResponse } from 'core/types';
import { getQueryStringToSnakeCase } from 'core/utils';

export const getUomConversionAuditReport = async (
  requestPayload: IUomConversionAuditReportRequestPayload,
  signal?: AbortSignal
): Promise<GetOneResponse<IUomConversionAuditReport>> => {
  const { data } = await partfinitiClient.get(
    `/company/uom-conversion-audit/report?${getQueryStringToSnakeCase(requestPayload, {
      removeNullValues: true,
    })}`,
    signal
  );

  const response = mapRecordFromServer<IUomConversionAuditReport>(data.data);

  if (!response.record) {
    return response;
  }

  return {
    ...response,
    record: {
      ...response.record,
      // The report has no natural row id: synthesize the stable key AG-Grid needs
      // from partId + extractedUom. A null and an empty extracted UOM are separate
      // rows server-side (separate GROUP BY buckets), so they must not collapse to
      // the same key here.
      rows:
        response.record.rows?.map((row) => ({
          ...row,
          id: `${row.partId}-${row.extractedUom === null ? '\u0000null' : row.extractedUom}`,
        })) ?? [],
    },
  };
};

/**
 * Assigns the same UOM values to every part in `partIds`.
 *
 * postForm snake-cases the keys, so `uoms` arrives as `uoms[uom_id]`,
 * `uoms[sales_uom]`… and the server writes only the keys it receives. An
 * explicitly empty value is sent (and clears that column); a field the user
 * left switched off is absent from the payload entirely.
 */
export const bulkUpdateUoms = async (
  payload: IUomConversionAuditBulkUpdatePayload
): Promise<IUomConversionAuditBulkUpdateResult> =>
  partfinitiClient.postForm('/company/uom-conversion-audit/bulk-update-uoms', payload, {
    includeNullValues: true,
  });

/**
 * Applies one set of company part details to every part in `partIds`, creating
 * the catalogue entry where the company has none and updating it where it
 * exists. A field left blank is left unchanged on the update leg.
 *
 * toSnakeCase is lodash's rather than the client default: the default leaves a
 * key ending in a digit alone, so `pricingGroup1`…`pricingGroupDescription5`
 * would go out camelCase and the server -- which matches SETUP_COLUMNS on
 * `pricing_group_1`… -- would drop every pricing group without an error.
 */
export const bulkSetUpParts = async (
  payload: IUomConversionAuditBulkSetUpPayload
): Promise<IUomConversionAuditBulkSetUpResult> =>
  partfinitiClient.postForm('/company/uom-conversion-audit/bulk-set-up-parts', payload, {
    toSnakeCase: snakeCase,
    includeNullValues: true,
  });

/**
 * Creates part-level UOM conversions in bulk through the same entity endpoint the
 * single-part form uses — `saveRecords` keys off the entity's plural name, so the
 * payload is an array under `partUomConversions`.
 */
export const bulkUpsertUomConversions = async (
  partUomConversions: IUomConversionAuditConversionPayload[]
): Promise<IUomConversionAuditBulkConversionResult> =>
  partfinitiClient.postForm('/uom/part-uom-conversion-edit/save-bulk', { partUomConversions });
